import { useMutation, useQueryClient } from "@tanstack/react-query";
import { launchCampaign } from "@/api/campaigns";
import { lotsKey, summaryKey } from "@/hooks/useCredits";
import { usePermissions } from "@/hooks/usePermissions";
import type { LaunchCampaignInput, LaunchCampaignResult } from "@/interfaces/review";
import { invalidateCampaignReads } from "@/utils/campaignCache-helper";

/**
 * The launch-and-pay mutation behind "Confirm & pay" — `POST /campaign-builder/launch` (DIV-7811).
 *
 * The server runs the whole launch in one transaction (campaign row → contacts → budget →
 * commitments), so a refusal creates nothing and needs no client-side compensation. Consumers
 * read `isPending` for the spinner, and `error` for the failure copy — pass it through
 * `apiErrorMessage()` so the server's own refusal sentence (e.g. which group is short and by
 * how much) reaches the buyer instead of axios's status-code wording.
 */
export function useLaunchCampaign() {
  const queryClient = useQueryClient();
  const { groupId } = usePermissions();

  return useMutation<LaunchCampaignResult, Error, LaunchCampaignInput>({
    mutationFn: (input) => launchCampaign(input),
    // A launch creates a campaign and spends credits, so every surface that counts either is
    // stale. Written now rather than with the endpoint: skipping it is what leaves a dashboard
    // showing the pre-launch list right after a successful checkout.
    onSuccess: () => {
      invalidateCampaignReads(queryClient);
      queryClient.invalidateQueries({ queryKey: ["reminders"] });
      if (groupId != null) {
        queryClient.invalidateQueries({ queryKey: summaryKey(groupId) });
        queryClient.invalidateQueries({ queryKey: lotsKey(groupId) });
      }
      queryClient.invalidateQueries({ queryKey: ["orgCreditBalance"] });
    },
  });
}
